import {notification as notify, Tooltip} from 'antd';
import {faClose, faCheck, faInfo, faExclamationTriangle, faCopy} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import classnames from 'classnames';
import React, {Fragment} from 'react';
import styles from './styles.module.scss';
import {faCircleCheck, faClockRotateLeft} from '@fortawesome/pro-solid-svg-icons';
import router from 'next/router';
import {ReactNode} from 'react';
import moment from 'moment';
import {getHttpStatusColor} from '../colors';
import {AxiosError} from 'axios';
import {openLiveChat} from '../intercom';
import {faExclamationCircle} from '@fortawesome/pro-solid-svg-icons';
import {Button} from '@/components/common-components/v2';
import {TracerReportButton} from '@/components/common-components/components/tracer-report-button';
import {getStore} from '@/store/root-store';

// Extend Window interface to include our error tracking
declare global {
  interface Window {
    __recentErrorNotifications?: Record<string, number>;
  }
}

export const antdNotification = {
  error(message: string, position?: any, duration?: number) {
    return notify.error({
      message,
      placement: position || 'bottomRight',
      key: 'error',
      duration: duration || 4.5,
    });
  },

  success(message: string, position?: any, duration?: number) {
    return notify.success({
      message,
      placement: position || 'bottomRight',
      duration: duration || 4.5,
    });
  },

  info(message: string, duration?: number) {
    return notify.info({
      message,
      placement: 'bottomRight',
      key: 'info',
      duration: duration || 4.5,
    });
  },
};

export const isExtensionInstalled = () => {
  const isActiveExtension = document.getElementById('contentGscRoot');
  return !!isActiveExtension;
};


export const newNotification = (message, delay, state) => {
  const key = `key${Date.now()}`;
  return notify.open({
    message:
      <Fragment>
        <div className={styles.notificationContent}>
          <span className={styles.title}>{message}</span>
        </div>
      </Fragment>,
    placement: 'topRight',
    duration: delay,
    key,
    className: classnames(styles.newNotification, styles.errorNotification),
    icon: <div><FontAwesomeIcon color={state == 'rollback' ? '#FFA500' : '#1FAC47'} icon={state == 'rollback' ? faClockRotateLeft : faCircleCheck} /></div>,
    closeIcon: <div className={styles.icon}><FontAwesomeIcon fontSize={16} color='white' icon={faClose} /></div>,
  });
};

interface customStatusProps {
  statuses: number[];
  msg?: string;
  msgFromResponse?: boolean;
  desc?: string;
  permanent?: boolean;
  closeMsg?: string;
  showDetails?: boolean;
}

interface ErrorNotificationProps {
  e?: any;
  msg?: string;
  desc?: string;
  permanent?: boolean;
  closeMsg?: string;
  handleStatuses?: customStatusProps[];
  isQuotaIssue?: boolean;
}

/**
 * @method useErrorNotification
 *
 * @param {any} e error event object
 * @param {string} msg custom msg to show as the error title
 * @param {string} desc custom desc to show as the error description
 * @param {boolean} permanent will set notification to stay on the screen permanently until closed manually
 * @param {string} closeMsg closing modal message
 * @param {customStatusType} handleStatuses array of custom handling of diff statuses
 */

const getResponseUrlWithoutDomain = url => {
  // eslint-disable-next-line no-useless-escape
  return url.replace(/^.*\/\/[^\/]+/, '');
};

export const useErrorNotification = ({
  e, msg = '', desc = '', permanent = false, closeMsg = 'Refresh page', handleStatuses = [], isQuotaIssue = true,
}: ErrorNotificationProps) => {
  if (msg === 'Network Error') {
    console.warn('notification error', msg);
    return;
  }
  const defaultHandle = () => notification.error(msg, desc, permanent, closeMsg, e.response?.status, e.request?.responseURL);
  const quotaDefaultHandle = e => {
    if (e?.response?.status === 429) {
      notification.error(msg, e.response?.data?.message, permanent, closeMsg, e.response?.status, e.request?.responseURL, true, isQuotaIssue);
    }
  };

  /*
  * HANDLE AXIOS EXCEPTIONS (quota 429 also goes here)
  * update notification with status and request url
  * status = e?.response?.status
  * url = e?.request?.responseURL
  */
  if (e && e instanceof AxiosError) {
    // if custom status handling not passed
    if (!handleStatuses.length) {
      if (e?.response?.status === 429) {
        quotaDefaultHandle(e);
        return;
      }
      defaultHandle();
      return;
    }

    // if custom status handling passed
    if (handleStatuses?.length) {
      // if quota is not handled in custom status array, we are handling it by default
      if (!handleStatuses.filter(statusItem => statusItem.statuses?.includes(429))?.length) {
        quotaDefaultHandle(e);
      }

      let allStatuses = [];
      handleStatuses.forEach(customItem => {
        allStatuses = [...allStatuses, ...(customItem.statuses ?? [])];
      });

      if (!allStatuses.includes(e?.response?.status)) {
        defaultHandle();
      } else {
        handleStatuses.forEach(statusItem => {
          if (statusItem.statuses?.includes(e?.response?.status)) {
            notification.error(statusItem.msg ?? '', statusItem.desc ?? e.response?.data?.message ?? '', statusItem.permanent, statusItem.closeMsg ?? '', e.response?.status, e.request?.responseURL, statusItem.showDetails ?? true);
          }
        });
      }
    }


    return;
  }

  /*
  * HANDLE OTHER EXCEPTIONS
  * show regular notifications
  * we are passing 0 as status, so notification knows that it has been called using this hook, and should show all advanced details
  * when notification is not called using this hook, we should not be showing details
  */
  notification.error(msg, desc, permanent, closeMsg, 0 );
};

export const notification = {
  error(message: string, description: string | ReactNode, permanent?:boolean, closeMessage:string='Refresh page', status?: number, failingUrl?: string, showDetails = true, isQuotaIssue = false, showTopup?: boolean, setTopUpBulkVisibility?: any, setShowDesc?: any, link?: string, hideContactSupport?: boolean, traceId?: string) {
    const store = getStore();
    const {settings: {customer: {profile}}} = store;
    const isGHLAccount = localStorage.getItem('isGHLAccount') === 'true';
    if (description === 'Network Error') {
      console.warn('notification error', description);
      return;
    }

    // Generate a key for this error to detect duplicates
    const errorKey = `${message}:${typeof description === 'string' ? description : 'ReactNode'}:${status}:${failingUrl || ''}`;

    // Check if we've shown this exact error notification recently
    if (typeof window !== 'undefined') {
      const recentErrors = window['__recentErrorNotifications'] || {};
      const now = Date.now();

      // Remove old entries from recentErrors (older than 2 seconds)
      Object.keys(recentErrors).forEach(key => {
        if (now - recentErrors[key] > 2000) {
          delete recentErrors[key];
        }
      });

      // If this exact error was shown in the last 2 seconds, don't show it again
      if (recentErrors[errorKey] && now - recentErrors[errorKey] < 2000) {
        return;
      }

      // Store this error with current timestamp
      recentErrors[errorKey] = now;
      window['__recentErrorNotifications'] = recentErrors;
    }

    // Track in global error tracker if available
    if (typeof window !== 'undefined' && window.__errorTracker) {
      window.__errorTracker.notificationErrors.push({
        type: 'notification',
        message,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        traceId,
        component: 'notification.error',
        handled: true,
        stack: new Error().stack,
      });
    }

    const key = `key${Date.now()}`;
    const dateStamp = moment(new Date()).utc().format('YYYY-MM-DD HH:mm:ss UTC');

    return notify.error({
      message:
        <div className={styles.notificationContentV2}>
          <div><FontAwesomeIcon icon={faExclamationCircle} color='#F44343'/></div>
          <div>
            <div className={styles.titleV2}>{message}</div>
            {description && (process.env.NODE_ENV !== 'production' || (process.env.NODE_ENV == 'production' && profile?.customerId == 439)) && <div className={styles.descriptionV2}>{description}</div>}
            <div className={styles.timeStampV2}>Detected: {dateStamp}</div>
            {status && showDetails && (process.env.NODE_ENV !== 'production' || (process.env.NODE_ENV == 'production' && profile?.customerId == 439)) ? <div>
              <div className={styles.issueTypeContainer}>Issue type: <span style={{fontWeight: 500, display: 'inlineBlock', marginLeft: 5}}>{isQuotaIssue ? 'Exhausted quota' : status == 0 ? 'FE' : 'API Fail'}</span></div>
              {status && !isQuotaIssue ? <div className={styles.issueTypeContainer}>Status: <span style={{color: getHttpStatusColor(status)}} className={styles.issueStatus}>{status}</span></div> : null}
              {failingUrl && !isQuotaIssue ? <div className={styles.issueTypeContainer}>Request URL: <a href={failingUrl} target='_blank' className={styles.issueUrl} rel='noreferrer'>{getResponseUrlWithoutDomain(failingUrl)}</a></div> : null}
            </div> : null}
            {traceId && <div className={styles.traceIdV2}>Trace ID: {traceId} <Tooltip title='Copy to clipboard'><FontAwesomeIcon style={{cursor: 'pointer'}} icon={faCopy} onClick={() => navigator.clipboard.writeText(traceId)}/></Tooltip></div>}
            <div className={styles.buttonsV2}>
              {!isGHLAccount && !hideContactSupport ? <Button background='#f443431a' className={styles.lightButton} onClick={() => openLiveChat(traceId)}>Contact support</Button> : null}
              {traceId && <TracerReportButton
                traceId={traceId}
                status={status}
                description={description}
                message={message}
              />}
              <Button background='#f443431a' className={styles.lightButton}>
                {showTopup ? <span className={styles.btn}
                  onClick={() => {
                    setShowDesc(false);
                    setTopUpBulkVisibility(true);
                  }}
                >
                  Top up
                </span> :
                  <span className={styles.btn} onClick={() => {
                    if (closeMessage == 'View Pricing') {
                      router.push('/pricing');
                    } else if (closeMessage == 'OK') {
                      notify.close(key);
                    } else {
                      location.reload();
                    }
                  }}>{closeMessage}</span>}
              </Button>
            </div>


          </div>

        </div>,
      placement: 'topRight',
      duration: permanent ? 0 : 10.5,
      key,
      icon: <div></div>,
      className: classnames(styles.mainNotification, styles.errorNotification, styles.notificationV2),
    });
  },
  success(message: string | ReactNode, description: string | ReactNode, closeMessage:string='Continue', onClickHandler?: any) {
    const key = `key${Date.now()}`;
    return notify.success({
      message:
        <div className={styles.notificationContent}>
          <span className={styles.title}>{message}</span>
          <span className={styles.description}>{description}</span>
          <span className={styles.btn} onClick={() => {
            onClickHandler && onClickHandler();
            notify.close(key);
          }}>{closeMessage}</span>
        </div>,
      placement: 'topRight',
      key,
      className: classnames(styles.mainNotification, styles.successNotification),
      icon: <div className={styles.icon}><FontAwesomeIcon icon={faCheck} /></div>,
    });
  },

  info(message: string, description: string, label?: string, action?: any, duration?: number) {
    const key = `key${Date.now()}`;

    const callback = () => {
      if (label) {
        return <span className={classnames(styles.btn, styles.contactSupportLink)} onClick={() => {
          if (action) {
            action();
          } else {
            return;
          }
        }}>{label}</span>;
      }

      return null;
    };

    return notify.info({
      message:
        <div className={styles.notificationContent}>
          <span className={styles.title}>{message}</span>
          <span className={styles.description}>{description}</span>
          <div className={styles.buttons}>
            <span className={styles.btn} onClick={() => notify.close(key)}>Dismiss</span>
            {callback()}
          </div>
        </div>,
      placement: 'topRight',
      key,
      className: classnames(styles.mainNotification, styles.infoNotification),
      icon: <div className={styles.icon}><FontAwesomeIcon icon={faInfo} /></div>,
      ...(duration && {duration}),
    });
  },

  warning(message: string | ReactNode, description: string | ReactNode, label?: string, action?: any) {
    const key = `key${Date.now()}`;
    const dateStamp = moment(new Date()).format('ll');
    const timeStampt = moment(new Date()).format('LT');
    const callback = () => {
      if (label) {
        return <span className={classnames(styles.btn, styles.contactSupportLink)} onClick={() => {
          if (action) {
            action();
          } else {
            return;
          }
        }}>{label}</span>;
      }

      return null;
    };

    return notify.info({
      message:
        <div className={styles.notificationContent}>
          <span className={styles.title}>{message}</span>
          <span className={styles.description}>{description}</span>
          <div className={styles.timeStamp}>
            {`Detected: ${dateStamp} at ${timeStampt}`}
          </div>
          <div className={styles.buttons}>
            <span className={styles.btn} onClick={() => notify.close(key)}>Dismiss</span>
            {callback()}
          </div>
        </div>,
      placement: 'topRight',
      key,
      className: classnames(styles.mainNotification, styles.warningNotification),
      icon: <div className={styles.icon}><FontAwesomeIcon icon={faExclamationTriangle} /></div>,
    });
  },
  destroy() {
    return notify.destroy();
  },
};
